/**
 * Parse API errors into user-facing messages and optional action hints.
 * Backend shapes vary: { error }, { detail }, { message }, field dicts.
 */

const DEFAULT_MESSAGES = {
  402: 'Payment is required to continue. Check billing in Account settings.',
  403: 'You do not have permission to do that.',
  429: 'Usage limit reached. Try again later or upgrade your plan.',
}

/**
 * @param {unknown} error - axios error or Error
 * @param {string} [fallback='Something went wrong. Please try again.']
 * @returns {{ message: string, status: number|null, upgradeUrl: string|null, quotaStatus: object|null }}
 */
export function parseApiError(error, fallback = 'Something went wrong. Please try again.') {
  const status = error?.response?.status ?? null
  const data = error?.response?.data

  let message = fallback

  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message.trim()) {
      message = data.message
    } else if (typeof data.error === 'string' && data.error.trim()) {
      message = data.error
    } else if (typeof data.detail === 'string' && data.detail.trim()) {
      message = data.detail
    } else if (data.errors && typeof data.errors === 'object') {
      const parts = Object.entries(data.errors).map(([field, msgs]) => {
        const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs)
        return `${field}: ${text}`
      })
      if (parts.length) {
        message = parts.join('\n')
      }
    }
  } else if (error instanceof Error && error.message) {
    message = error.message
  } else if (typeof error === 'string') {
    message = error
  }

  if (status && message === fallback && DEFAULT_MESSAGES[status]) {
    message = DEFAULT_MESSAGES[status]
  }

  const upgradeUrl =
    (data && typeof data.upgrade_url === 'string' && data.upgrade_url) ||
    (data?.quota_status && typeof data.quota_status.upgrade_url === 'string'
      ? data.quota_status.upgrade_url
      : null) ||
    null

  const quotaStatus =
    (data && data.quota_status && typeof data.quota_status === 'object'
      ? data.quota_status
      : null) || null

  return { message, status, upgradeUrl, quotaStatus }
}

/**
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback) {
  return parseApiError(error, fallback).message
}

/**
 * Show a toast for an API error when window.$toast is available.
 * @param {unknown} error
 * @param {{ fallback?: string, type?: 'error'|'warning'|'info' }} [options]
 * @returns {boolean} whether a toast was shown
 */
export function showGlobalApiError(error, options = {}) {
  const { fallback, type = 'error' } = options
  const parsed = parseApiError(error, fallback)
  const toast = typeof window !== 'undefined' ? window.$toast : null
  if (!toast || typeof toast[type] !== 'function') {
    return false
  }

  let text = parsed.message
  if (parsed.status === 429 && parsed.upgradeUrl) {
    text = `${parsed.message} Open Billing & Subscription from Account settings.`
  }

  toast[type](text)
  return true
}
